import React, { useState, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';

const PRISM_VERSION = '1.29.0';
const PRISM_BASE = `https://cdn.jsdelivr.net/npm/prismjs@${PRISM_VERSION}`;
const MAX_PREVIEW_SIZE = 1024 * 1024;

const EXT_TO_LANG = {
  js: 'javascript', mjs: 'javascript', cjs: 'javascript',
  jsx: 'jsx', ts: 'typescript', tsx: 'tsx',
  py: 'python', pyw: 'python',
  rb: 'ruby',
  go: 'go',
  java: 'java',
  c: 'c', h: 'c',
  cpp: 'cpp', cc: 'cpp', cxx: 'cpp', hpp: 'cpp',
  cs: 'csharp',
  php: 'php',
  rs: 'rust',
  swift: 'swift',
  kt: 'kotlin', kts: 'kotlin',
  sql: 'sql',
  css: 'css', scss: 'scss', less: 'less', sass: 'sass',
  bash: 'bash', zsh: 'bash',
  ps1: 'powershell',
  lua: 'lua',
  r: 'r',
  pl: 'perl', pm: 'perl',
  dart: 'dart',
  vue: 'markup', svelte: 'markup',
  m: 'objectivec', mm: 'objectivec',
  scala: 'scala',
  clj: 'clojure', cljs: 'clojure',
  hs: 'haskell',
  elm: 'elm',
  jl: 'julia',
  toml: 'toml',
  graphql: 'graphql', gql: 'graphql',
  tf: 'hcl', tfvars: 'hcl',
  proto: 'protobuf',
  sh: 'bash',
};

let prismReady = null;
function ensurePrism() {
  if (prismReady) return prismReady;
  if (typeof window !== 'undefined' && window.Prism && window.Prism.plugins?.autoloader) {
    prismReady = Promise.resolve(window.Prism);
    return prismReady;
  }
  prismReady = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-prism-theme]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `${PRISM_BASE}/themes/prism-tomorrow.min.css`;
      link.dataset.prismTheme = 'true';
      document.head.appendChild(link);
    }
    const core = document.createElement('script');
    core.src = `${PRISM_BASE}/prism.min.js`;
    core.async = true;
    core.onerror = () => reject(new Error('Failed to load Prism core'));
    core.onload = () => {
      window.Prism.manual = true;
      const al = document.createElement('script');
      al.src = `${PRISM_BASE}/plugins/autoloader/prism-autoloader.min.js`;
      al.async = true;
      al.onerror = () => reject(new Error('Failed to load Prism autoloader'));
      al.onload = () => {
        if (window.Prism?.plugins?.autoloader) {
          window.Prism.plugins.autoloader.languages_path = `${PRISM_BASE}/components/`;
        }
        resolve(window.Prism);
      };
      document.head.appendChild(al);
    };
    document.head.appendChild(core);
  });
  return prismReady;
}

const CodePreview = memo(({ src, fileName }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const codeRef = useRef(null);

  const ext = (fileName?.split('.').pop() || src?.split('?')[0].split('.').pop() || '').toLowerCase();
  const lang = EXT_TO_LANG[ext] || 'plaintext';

  useEffect(() => {
    if (!src) return;
    setLoading(true);
    setError(null);

    Promise.all([
      fetch(src).then((r) => {
        if (!r.ok) throw new Error('Failed to load file');
        const cl = r.headers.get('content-length');
        if (cl && parseInt(cl) > MAX_PREVIEW_SIZE) {
          throw new Error('File too large to preview');
        }
        return r.text();
      }),
      ensurePrism().catch(() => null),
    ])
      .then(([text, Prism]) => {
        let body = text;
        if (body.length > MAX_PREVIEW_SIZE) {
          body = body.substring(0, MAX_PREVIEW_SIZE) + '\n\n--- File truncated (exceeds 1MB preview limit) ---';
        }
        setCode(body);
        setLoading(false);
        if (Prism) {
          requestAnimationFrame(() => {
            if (codeRef.current) Prism.highlightElement(codeRef.current);
          });
        }
      })
      .catch((err) => {
        console.error('Code preview error:', err);
        setError(err.message || 'Unable to load code file');
        setLoading(false);
      });
  }, [src, lang]);

  const handleCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  if (loading) {
    return <div className="u_fv-code-preview u_fv-code-preview--loading"><p>Loading...</p></div>;
  }
  if (error) {
    return <div className="u_fv-code-preview u_fv-code-preview--error"><p>{error}</p></div>;
  }

  return (
    <div className="u_fv-code-preview">
      <div className="u_fv-code-toolbar">
        <span className="u_fv-code-lang">{lang}</span>
        <button type="button" className="u_fv-code-copy" onClick={handleCopy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className={`u_fv-code-content language-${lang}`}>
        <code ref={codeRef} className={`language-${lang}`}>{code}</code>
      </pre>
    </div>
  );
});

CodePreview.propTypes = {
  src: PropTypes.string.isRequired,
  fileName: PropTypes.string,
};

export default CodePreview;
